import React from 'react';
import { useEditor } from '@grapesjs/react';
import { Property, PropertyNumber, PropertyComposite, PropertyRadio, PropertySelect, PropertyStack } from 'grapesjs';
import {
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Button,
  Slider,
  Box,
  Stack,
  IconButton,
  Typography,
} from '@mui/material';
import { AntDesign, MaterialIcons, MaterialCommunityIcons } from '@expo/vector-icons';
const Add = (props: any) => <AntDesign name="plus" {...props} />;
const Delete = (props: any) => <MaterialIcons name="delete" {...props} />;
const KeyboardArrowUp = (props: any) => <MaterialCommunityIcons name="arrow-up-bold" {...props} />;
const KeyboardArrowDown = (props: any) => <MaterialCommunityIcons name="arrow-down-bold" {...props} />;

// Main Component
export default function StylePropertyField({ prop }: { prop: Property }) {
  const editor = useEditor();
  const [_, setCounter] = React.useState(0);

  // This effect listens for style-related changes and forces a re-render.
  // This is the key to ensuring the component updates when the state changes.
  React.useEffect(() => {
    const forceUpdate = () => setCounter(c => c + 1);
    const events = 'component:styleUpdate state:select';
    editor.on(events, forceUpdate);
    return () => { editor.off(events, forceUpdate) };
  }, [editor]);

  const handleChange = React.useCallback((value: string, partial = false) => {
    prop.upValue(value, { partial });
  }, [prop]);

  const openAssets = React.useCallback(() => {
    editor.Assets.open({
      select: (asset, complete) => {
        prop.upValue(asset.getSrc(), { partial: !complete });
        if (complete) {
          editor.Assets.close();
        }
      },
      types: ['image'],
    });
  }, [editor, prop]);

  const type = prop.getType();
  const defValue = prop.getDefaultValue();
  const canClear = prop.canClear();
  const value = prop.getValue();
  const valueString = prop.hasValue() ? value : '';

  const commonProps = {
    fullWidth: true,
    size: 'small' as 'small',
    variant: 'outlined' as 'outlined',
  };

  const renderLabel = () => (
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
        {prop.getLabel()}
      </Typography>
      {canClear && (
        <IconButton size="small" onClick={() => prop.clear()}>
          <Delete fontSize="small" />
        </IconButton>
      )}
    </Stack>
  );

  switch (type) {
    case 'radio': {
      const radioProp = prop as PropertyRadio;
      return (
        <FormControl fullWidth>
          <Typography variant="body2" sx={{ textTransform: 'capitalize', mb: 1 }}>{radioProp.getLabel()}</Typography>
          <RadioGroup value={radioProp.getValue()} onChange={(e) => handleChange(e.target.value)}>
            <Stack direction="row" spacing={2} flexWrap="wrap">
              {radioProp.getOptions().map((option) => (
                <FormControlLabel
                  key={radioProp.getOptionId(option)}
                  value={radioProp.getOptionId(option)}
                  control={<Radio size="small" />}
                  label={radioProp.getOptionLabel(option)}
                />
              ))}
            </Stack>
          </RadioGroup>
        </FormControl>
      );
    }

    case 'select': {
      const selectProp = prop as PropertySelect;
      return (
        <FormControl {...commonProps}>
          <InputLabel>{selectProp.getLabel()}</InputLabel>
          <Select value={value} onChange={(e) => handleChange(e.target.value)} label={selectProp.getLabel()}>
            {selectProp.getOptions().map((option) => (
              <MenuItem key={selectProp.getOptionId(option)} value={selectProp.getOptionId(option)}>
                {selectProp.getOptionLabel(option)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }

    case 'color': {
      return (
        <Box sx={{marginBlock: 2}}>
          {renderLabel()}
          <TextField
            {...commonProps}
            placeholder={defValue}
            value={valueString}
            onChange={(e) => handleChange(e.target.value)}
            InputProps={{
              startAdornment: (
                <Box sx={{ mr: 1, width: 20, height: 20, bgcolor: value, border: '1px solid #ccc' }} />
              ),
              endAdornment: (
                <input type="color" value={value} onChange={(e) => handleChange(e.target.value)} style={{ border: 'none', width: 25, height: 25, padding: 0, background: 'none' }} />
              )
            }}
          />
        </Box>
      );
    }

    case 'number': {
      const numberProp = prop as PropertyNumber;
      const units = numberProp.getUnits();
      return (
        <Box sx={{marginBlock: 2}}>
          {renderLabel()}
          <Stack direction="row" spacing={1}>
            <TextField
              sx={{ flexGrow: 1, minWidth: 130 }}
              size="small"
              variant="outlined"
              type="number"
              placeholder={defValue}
              value={numberProp.getValue({ noDefault: true })}
              onChange={(e) => handleChange(e.target.value, true)}
              onBlur={() => handleChange(value, false)}
            />
            {units.length > 0 && (
              <FormControl size="small">
                <Select
                  sx={{ minWidth: 65 }}
                  value={numberProp.getUnit()}
                  onChange={(e) => handleChange(`${numberProp.getValue({ noDefault: true })}${e.target.value}`, true)}
                >
                  {units.map((unit) => <MenuItem key={unit} value={unit}>{unit}</MenuItem>)}
                </Select>
              </FormControl>
            )}
          </Stack>
        </Box>
      );
    }

    case 'slider': {
      const sliderProp = prop as PropertyNumber;
      return (
        <Box sx={{marginBlock: 2}}>
          {renderLabel()}
          <Slider
            value={parseFloat(value) || 0}
            min={sliderProp.getMin()}
            max={sliderProp.getMax()}
            step={sliderProp.getStep()}
            onChange={(e, v) => handleChange(String(v))}
            valueLabelDisplay="auto"
          />
        </Box>
      )
    }

    case 'file': {
      return (
        <Box sx={{marginBlock: 2}}>
          {renderLabel()}
          <Stack direction="row" spacing={2} alignItems="center">
            <Button {...commonProps} variant="contained" onClick={openAssets}>Select Image</Button>
            {value && value !== defValue && <Typography variant="caption" noWrap>{value.split('/').pop()}</Typography>}
          </Stack>
        </Box>
      );
    }

    case 'composite': {
      const compositeProp = prop as PropertyComposite;
      return (
        <Box sx={{marginBlock: 2}}>
          {renderLabel()}
          <Stack direction="row" flexWrap="wrap" spacing={1} sx={{ mt: 1, p: 1, border: '1px solid #ccc', borderRadius: 1 }}>
            {compositeProp.getProperties().map((prop) => (
              <Box key={prop.getId()} sx={{ flexBasis: 'calc(50% - 4px)', flexGrow: 1 }}>
                <StylePropertyField prop={prop} />
              </Box>
            ))}
          </Stack>
        </Box>
      );
    }

    case 'stack': {
      const stackProp = prop as PropertyStack;
      const layers = stackProp.getLayers();
      const selectedLayer = stackProp.getSelectedLayer();
      const [_, setLayerCounter] = React.useState(0);
      React.useEffect(() => {
        const forceUpdate = () => setLayerCounter(c => c + 1);
        editor.on('style:layer:select', forceUpdate);
        return () => { editor.off('style:layer:select', forceUpdate) };
      }, [editor]);

      return (
        <Stack spacing={1}>
          {renderLabel()}
          <Button startIcon={<Add />} size="small" onClick={() => stackProp.addLayer({}, { at: 0 })}>Add Layer</Button>
          {layers.map((layer) => (
            <Stack
              key={layer.getId()}
              direction="row"
              alignItems="center"
              onClick={() => layer.select()}
              sx={{
                p: 0.5,
                cursor: 'pointer',
                borderRadius: 1,
                bgcolor: layer.isSelected() ? 'primary.light' : 'action.hover',
              }}
            >
              <Typography variant="body2" sx={{ flexGrow: 1, fontWeight:"bold" }}>{layer.getLabel()}</Typography>
              <IconButton size="small" onClick={(e) => { e.stopPropagation(); layer.move(layer.getIndex() - 1); }}><KeyboardArrowUp /></IconButton>
              <IconButton size="small" onClick={(e) => { e.stopPropagation(); layer.move(layer.getIndex() + 1); }}><KeyboardArrowDown /></IconButton>
              <IconButton size="small" onClick={(e) => { e.stopPropagation(); layer.remove(); }}><Delete /></IconButton>
            </Stack>
          ))}
          {selectedLayer && (
            <Box sx={{ p: 1.5, border: '1px solid #ddd', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>Layer Properties</Typography>
              <Stack spacing={2}>
                {selectedLayer.get("values").map((prop) => (
                  <StylePropertyField key={prop.getId()} prop={prop} />
                ))}
              </Stack>
            </Box>
          )}
        </Stack>
      );
    }

    default: {
      return (
        <Box sx={{marginBlock: 2}}>
          {renderLabel()}
          <TextField
            {...commonProps}
            placeholder={defValue}
            value={valueString}
            onChange={(e) => handleChange(e.target.value)}
          />
        </Box>
      );
    }
  }
}
